import type {SupabaseClient} from "@supabase/supabase-js";
import type {Database} from "../types/database.types";

/**
 * Structural type alias for SupabaseClient<Database>.
 * Uses a mapped type to avoid nominal type checking for protected properties
 * (e.g., supabaseUrl), which causes false positives in pnpm monorepos.
 */
export type DbClient = {[K in keyof SupabaseClient<Database>]: SupabaseClient<Database>[K]};
export type ActivityType = "phone" | "message" | "visit" | "letter";

export interface CreateActivityInput {
  store_id: string;
  customer_id: string;
  hostess_id: string;
  activity_type: ActivityType;
  activity_date?: string;
  notes?: string | null;
}

export interface CancelActivityInput {
  id: string;
  cancel_reason?: string | null;
  cancelled_by?: string | null;
}

export interface UpdateActivityDateInput {
  id: string;
  activity_date: string;
}

export interface ListActivitiesInput {
  customer_id?: string;
  hostess_id?: string;
  store_id?: string;
  store_ids?: string[];
  activity_type?: ActivityType;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
  include_relations?: boolean;
}

export interface ActivityStatsInput {
  customer_id: string;
  hostess_id?: string;
  store_id?: string;
  days?: number;
}

export interface HostessActivityStatsInput {
  hostess_id: string;
  days?: number;
}

export interface ActivityCounts {
  phone_count: number;
  message_count: number;
  visit_count: number;
  letter_count: number;
  total_count: number;
}

export interface BatchActivityStatsInput {
  customer_ids: string[];
  store_id?: string;
  days?: number;
}

// --- ホステス営業インサイト（admin 詳細「営業タブ」用） ---

export interface HostessActivityInsightsInput {
  hostess_id: string;
  /** 勧誘間口の対象月 'yyyy-MM'（JST 暦月）。未指定なら当月 */
  month?: string;
  /**
   * 当月表示時の締め日。false/未指定=前日まで（既定）、true=本日まで（集計中）。
   * 過去月では無視され常に月末まで。月初(1日)で前日が前月に落ちる場合は本日へフォールバック。
   */
  include_today?: boolean;
  /** 勧誘間口の顧客リスト上限（既定 100。distinct_customer_count はこの上限に依らず真の総数） */
  customer_limit?: number;
}

/** 勧誘間口: その月に営業した顧客 1 件分（誰に何回・種別内訳・直近営業日 JST） */
export interface OutreachCustomer {
  customer_id: string;
  customer_name: string | null;
  activity_count: number;
  phone_count: number;
  message_count: number;
  visit_count: number;
  letter_count: number;
  /** JST 暦日 'yyyy-MM-dd' */
  last_activity_date: string;
}

/** 勧誘間口情報: その月に何人へ営業したか + 内訳 */
export interface OutreachBreadth {
  /** JST 暦日 'yyyy-MM-dd'（対象月の初日） */
  month_from: string;
  /** JST 暦日 'yyyy-MM-dd'（対象月の末日） */
  month_to: string;
  /** その月に営業をかけた distinct 顧客数 */
  distinct_customer_count: number;
  /** その月の総営業数 */
  total_activity_count: number;
  /** 誰に営業したか（件数降順・上限 customer_limit） */
  customers: OutreachCustomer[];
}

/** サマリー: ある 1 日の営業数（JST 暦日。営業ゼロの日も含む） */
export interface DailyActivityCount {
  /** JST 暦日 'yyyy-MM-dd' */
  activity_date: string;
  total_count: number;
  phone_count: number;
  message_count: number;
  visit_count: number;
  letter_count: number;
}

/** get_hostess_activity_insights RPC の戻り値 */
export interface HostessActivityInsights {
  outreach: OutreachBreadth;
  /** 直近 N 日の日毎営業数（古い→新しい順、ゼロ埋め済み） */
  daily: DailyActivityCount[];
}

// しきい値プリセット（統一: 過去30日で1件以上 = good, 0件 = poor）
export const ACTIVITY_THRESHOLDS = {
  evaluate: (total: number): "good" | "poor" => (total >= 1 ? "good" : "poor"),
};
